import { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { BaseUrlContext } from '@core/context';
import getBrowserRouterPath from '@core/utils/getBrowserRouterPath';

/**
 * Exposes a proxy around `window.location` that can be accessed via `window.locationProxy`.
 * The proxy intercepts navigation attempts and redirects them through the React Router's history
 * API, or falls back to the original `window.location` methods if the path does not match any
 * browser router paths.
 *
 * Some users may write custom JavaScript that manipulates `window.location` in the Hub in order to
 * programmatically navigate. When this type of navigation occurs within the iframe, it can lead to
 * a crash of the iframe preview because these are called without the ?isFramePreview query param
 * that sets the X-Frame-Options header correctly in our express middleware.
 *
 * We intercept these events using the web Navigation API in `components/FramePreview`, but this API
 * is not yet supported in all browsers. As a fallback, this proxy around `window.location` can be
 * used in custom JS to ensure that navigation is handled correctly in admin mode in non-chromium browsers.
 */
export default function ExposeLocationProxy() {
  const baseUrl = useContext(BaseUrlContext);
  const history = useHistory();

  // Expose the locationProxy on the window object inline instead of using a useEffect hook
  // to ensure it is available ahead of our react app mounting.
  if (typeof window !== 'undefined' && !window.readme?.location) {
    window.readme ??= {};

    const locationProxy = {
      get href() {
        return window.location.href;
      },
      set href(value: string) {
        const routerPath = getBrowserRouterPath(value, baseUrl);
        if (routerPath) {
          history.push(routerPath);
        } else {
          window.location.href = value;
        }
      },
      assign(url: string) {
        const routerPath = getBrowserRouterPath(url, baseUrl);
        if (routerPath) {
          history.push(routerPath);
        } else {
          window.location.assign(url);
        }
      },
      replace(url: string) {
        const routerPath = getBrowserRouterPath(url, baseUrl);
        if (routerPath) {
          history.replace(routerPath);
        } else {
          window.location.replace(url);
        }
      },
      // Passing through the search property without modifying it
      get search() {
        return window.location.search;
      },
      set search(value: string) {
        window.location.search = value;
      },
    };

    Object.defineProperty(window.readme, 'location', {
      configurable: true,
      value: locationProxy,
    });
  }

  return null;
}
